import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map, of, switchMap, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { IFactory } from '../../applicant/models/i-factory';
import { Impresa } from '../../applicant/models/impresa';
import { LiberoProfessionista } from '../../applicant/models/libero-professionista';
import { PersonaGiuridica } from '../../applicant/models/persona-giuridica';
import { AddressFormData } from '../../_widgets/address-form/address-form-data';
import { ImpresaFactory } from '../../applicant/models/impresa-factory';
import { Target } from '../../../interfaces/pratica';
import { ProfessionistaFactory } from '../../applicant/models/professionista-factory';

@Injectable({
  providedIn: 'root',
})
export class PersonaGiuridicaService {
  private readonly queryEndpoint = `${environment.api}/persona-giuridica-query`;
  private readonly commandEndpoint = `${environment.api}/persona-giuridica-command`;

  cachedPersonaGiuridica: LiberoProfessionista | Impresa | undefined;

  constructor(private http: HttpClient) {}

  getById(
    idPratica: string,
    target: Target
  ): Observable<LiberoProfessionista | Impresa> {
    if (this.cachedPersonaGiuridica) {
      return of(this.cachedPersonaGiuridica);
    }
    let factory = this.getFactoryByTarget(target);
    return this.getPersonaGiuridica(idPratica, factory);
  }

  getByIdWithoutCache(
    idPratica: string,
    target: Target
  ): Observable<LiberoProfessionista | Impresa> {
    let factory = this.getFactoryByTarget(target);
    return this.getPersonaGiuridica(idPratica, factory);
  }

  edit<F extends LiberoProfessionista | Impresa>(
    target: Target,
    updatedData: Partial<F>,
    personaGiuridicaId: string,
    tipoContabilita?: PersonaGiuridica['tipoContabilita'],
    kycData?: Impresa['kic'],
    location?: Partial<AddressFormData>
  ) {
    let factory = this.getFactoryByTarget(target);
    let body = factory.toDomain(
      updatedData,
      tipoContabilita,
      kycData,
      location
    );
    return this.http
      .patch(
        `${this.commandEndpoint}/${factory.endpointPath}/${personaGiuridicaId}`,
        body
      )
      .pipe(
        switchMap((res) =>
          this.getPersonaGiuridica(personaGiuridicaId, factory, false)
        )
      );
  }

  private getPersonaGiuridica<
    B extends object,
    F extends LiberoProfessionista | Impresa
  >(id: string, factory: IFactory<B, F>, byPratica = true) {
    return this.http
      .get<B>(
        `${this.queryEndpoint}/${factory.endpointPath}${
          byPratica ? '/pratica' : ''
        }/${id}`
      )
      .pipe(
        map((res) => factory.toView(res)),
        tap((pg) => (this.cachedPersonaGiuridica = pg))
      );
  }

  cleanData() {
    this.cachedPersonaGiuridica = undefined;
  }

  getFactoryByTarget(target: Target) {
    let factoryRecord: Record<
      Target,
      IFactory<object, Impresa | LiberoProfessionista>
    > = {
      [Target.IMPRESE]: new ImpresaFactory(),
      [Target.PROFESSIONISTI]: new ProfessionistaFactory(),
    };
    return factoryRecord[target];
  }
}
